import { useForm, Controller, FormProvider } from "react-hook-form";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { EditBookingStatusForm } from "./bookingStatus.schema";
import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { useEffect } from "react";
import { useUpdateBookingStatus } from "@/src/lib/bookingStatusMutation";
import { useToast } from "@/src/components/ui/use-toast";
import { Booking } from "../model";

type EditBookingStatusModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialStatus: string;
  bookingId: string;
  brand: Booking["brand"];
};

export const EditBookingStatusModal = ({
  open,
  setOpen,
  initialStatus,
  brand,
  bookingId,
}: EditBookingStatusModalProps) => {
  const updateBookingStatus = useUpdateBookingStatus(bookingId);
  const { toast } = useToast();
  const form = useForm<EditBookingStatusForm>({
    defaultValues: {
      status: initialStatus,
      brand,
    },
  });

  const { handleSubmit, control, reset } = form;

  useEffect(() => {
    reset({ status: initialStatus, brand });
  }, [initialStatus, brand, reset]);

  const onSubmit = (data: EditBookingStatusForm) => {
    const toastId = toast({
      description: "Updating payment schedule...",
      variant: "default",
      duration: Infinity,
    }).id;
    updateBookingStatus.mutate({ status: data.status, brand: data.brand, toastId });
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-[480px] max-h-[calc(100%-60px)] overflow-y-auto">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle className="text-3xl font-semi-bold">Booking status</DialogTitle>
            </DialogHeader>
            <div className="flex gap-6">
              <div className="mt-4 flex-1">
                <FormField
                  key="status"
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <FormControl>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value}>
                              <SelectTrigger>
                                <SelectValue placeholder="Status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new_booking">New Booking</SelectItem>
                                <SelectItem value="booking_checked">Booking Checked</SelectItem>
                                <SelectItem value="on_charter">On Charter</SelectItem>
                                <SelectItem value="completed_charter">Completed Charter</SelectItem>
                                <SelectItem value="booking_cancelled">Booking Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-4 flex-1">
                <FormField
                  key="brand"
                  control={control}
                  name="brand"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Brand</FormLabel>
                      <FormControl>
                        <Controller
                          name="brand"
                          control={control}
                          render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value}>
                              <SelectTrigger>
                                <SelectValue placeholder="Brand" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="helm">Helm</SelectItem>
                                <SelectItem value="anchor">Anchor</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter className="flex mt-8 w-full !justify-between">
              <Button type="button" onClick={() => setOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" variant="default">
                Submit
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
